import './sign-in-form.scss'
import Logo from 'assets/authentication/amorino-logo.svg'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FormState } from '../login/login';
import { StringStore } from 'StringStore';
import { useDispatch, useSelector } from 'react-redux';
import { auth0Login } from '../../authSlices';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AppRoutes } from '../../types/app-routes';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { UserRoles } from 'features/common/components/SideBar/SideBar';
import { createUser } from '../../userSlices';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    lastname: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
});

export interface SignInFormProps {
    name: string;
    lastname: string;
    email: string;
}

const SignInForm = ({setFormState}: {setFormState: Function}) => {
    const dispatch: Function = useDispatch()
    const isAuthenticated = useSelector((state: any) => state.auth.isAuthenticated)
    const isLoading = useSelector((state: any) => state.user.isLoading)
    const user = useSelector((state: any) => state.auth.user)
    const isFirstLogin = user?.firstLogin
    const loginError = useSelector((state: any) => state.auth.error)
    const navigate = useNavigate()
    const [error, setError] = useState('');
    const isCreateUserError = useSelector((state: any) => state.user.isCreateUserError)
    const [successMessage, setSuccessMessage] = useState('');
    const isSuccess = useSelector((state: any) => state.user.isSuccess)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    
    useEffect(() => {
        if (isAuthenticated && isFirstLogin){
            setFormState(FormState.FirstLogin)
        }
        else if(isAuthenticated){
            if(user.roleLevel >= UserRoles.MANAGER)
                navigate(AppRoutes.DASHBOARD)
            else navigate(`${AppRoutes.STOCK_MANAGEMENT}/ice-cream`)
        }
    }, [isAuthenticated, navigate, isFirstLogin])

    useEffect(() => {
        if (isCreateUserError) {
            setError('Failed to create user. Please try again.');
            setSuccessMessage('');
        }
    }, [isCreateUserError]);

    useEffect(() => {
        if (isSuccess) {
            setSuccessMessage('Please check your email for confirmation and password details');
            setError('');
            setTimeout(() => {
                setSuccessMessage('');
                setError('');
                setFormState(FormState.Login);
            }, 3000);
        }
    }, [isSuccess]);

    const handleSubmit = () => {
        formik.handleSubmit();
    }

    const formik = useFormik<SignInFormProps>({
        initialValues: {
            name: '',
            lastname: '',
            email: ''
        },
        validateOnMount: true,
        validationSchema,
        onSubmit: values => {
            const newUser = {
              name: values.name,
              lastname: values.lastname,
              email: values.email,
              shops: [],
              position: 3
            }
            
            dispatch(createUser(newUser, ''));
            setShowConfirmDialog(false);
        }
    });


    return (
        <div className="login-form">
            <div className="login-form__header">
                <img src={Logo} alt="Logo" className="login-form__logo"/>
                <div className="login-form__titles">
                    <div className="login-form__title">{'Sign Up'}</div>
                    <div className="login-form__subtitle">{'Create an account to get started'}</div>
                </div>
            </div>
            <div className="login-form__inputs">
                <div className="login-form__inputs__input-combo">
                    <label htmlFor="name" className='login-form__inputs__label'>Name</label>
                    <InputText 
                        id="name" 
                        name="name" 
                        onChange={formik.handleChange} 
                        className='login-form__inputs__text-input' 
                        placeholder="Enter your name" 
                    />
                </div>
                <div className="login-form__inputs__input-combo">
                    <label htmlFor="lastname" className='login-form__inputs__label'>Last Name</label>
                    <InputText 
                        id="lastname" 
                        name="lastname" 
                        onChange={formik.handleChange} 
                        className='login-form__inputs__text-input' 
                        placeholder="Enter your last name" 
                    />
                </div>
                <div className="login-form__inputs__input-combo">
                    <label htmlFor="email" className='login-form__inputs__label'>Email</label>
                    <InputText 
                        id="email" 
                        name="email" 
                        onChange={formik.handleChange} 
                        className='login-form__inputs__text-input' 
                        placeholder="Enter your email" 
                    />
                </div>
                
                {successMessage && (
                    <div className="login-form__success">
                        {successMessage}
                    </div>
                )}
                
                {error && (
                    <div className="login-form__error">
                        {error}
                    </div>
                )}
                
                <Button 
                    type='submit' 
                    disabled={!formik.isValid || isLoading} 
                    onClick={() => setShowConfirmDialog(true)} 
                    label={isLoading ? '' : "Sign Up"}
                    icon={isLoading ? 'pi pi-spinner pi-spin' : undefined}
                    raised 
                    className="login-form__inputs__button" 
                />
                
                {showConfirmDialog && (
                    <>
                        <div className="mask"></div>
                        <div className="popup">
                            <div className="confirmation-popup visible">
                                <div className="confirmation-popup__header">
                                    <div className="confirmation-popup__header__titles">
                                        <div className="confirmation-popup__header__titles__title">
                                            Create Owner Account
                                        </div>
                                    </div>
                                    <Button 
                                        onClick={() => setShowConfirmDialog(false)} 
                                        icon="pi pi-times" 
                                        className="confirmation-popup__header__close-button"
                                        label=""
                                    />
                                </div>
                                <div className="confirmation-popup__content">
                                    <div className="confirmation-popup__content__warning">
                                    This action will create a new owner account. If you are not the shop owner, please ask the shop owner to create an account for you directly.
                                    </div>
                                    <div className="confirmation-popup__content__title">
                                        Are you sure you want to proceed?
                                    </div>
                                </div>
                                <div className="confirmation-popup__footer">
                                    <Button 
                                        onClick={() => setShowConfirmDialog(false)} 
                                        label="Cancel" 
                                        className="confirmation-popup__footer__cancel-button" 
                                    />
                                    <Button 
                                        onClick={()=>handleSubmit()} 
                                        label="Confirm" 
                                        className="confirmation-popup__footer__confirm-button" 
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}
                
                <div className='login-form__help'>
                    Already have an account?
                    <div 
                        className='login-form__click'
                        onClick={() => setFormState(FormState.Login)}
                    >
                        Sign in
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SignInForm;